import _ from "underscore";
import { Environment, EnvironmentHelper, ModelTypeHelper } from "@biteinc/enums";
import { Log } from "@biteinc/common";
import { BiteAsync } from "@biteinc/helpers-universal";
//#region src/models/base_abstract_model.ts
var BaseAbstractModel = class {
	static CollectionName;
	static Type;
	static DatabaseClientKey;
	static ExclusionFields = [];
	isNew = false;
	static QueryBuilder;
	inflateData(data, _inflateFullData) {
		return data;
	}
	get id() {
		return this.data._id;
	}
	get Type() {
		return this.constructor.Type;
	}
	get DatabaseClientKey() {
		return this.constructor.DatabaseClientKey;
	}
	get ExclusionFields() {
		return this.constructor.ExclusionFields;
	}
	isType(ModelClass) {
		return this.Type === ModelClass.Type;
	}
	has(prop) {
		return _.has(this.data, prop);
	}
	/**
	* @deprecated This should not be used. Type your sets
	*/
	setUntyped(prop, value) {
		this.data[prop] = value;
	}
	set(prop, value) {
		this.data[prop] = value;
	}
	async setWithDict(setDict) {
		return this.saveWithDict({ $set: setDict });
	}
	async insert(session) {
		return this.saveWithDict({ $set: this.data }, session);
	}
	async saveWithDict(updateDict, session) {
		return this.saveWithDictAndArrayFilters(updateDict, void 0, session);
	}
	async saveWithDictIfMatches(condition, updateDict) {
		const filterQuery = {
			...condition,
			_id: this.id
		};
		const updateQuery = updateDict;
		const newData = await this.db().findOneAndUpdate(filterQuery, updateQuery, { returnDocument: "after" });
		if (!newData) return false;
		this.data = newData;
		return true;
	}
	async saveWithDictAndArrayFilters(updateDict, arrayFilters, session) {
		if (this.id && !this.isNew) {
			if (updateDict.$set && updateDict.$set === this.data) delete updateDict.$set.updatedAt;
			const filterQuery = { _id: this.id };
			const updateQuery = updateDict;
			const newData = await this.db().findOneAndUpdate(filterQuery, updateQuery, {
				returnDocument: "after",
				...arrayFilters && { arrayFilters },
				session
			});
			if (!newData) {
				const errMessage = `The ${ModelTypeHelper.name(this.Type)} you were looking for could not be found.`;
				throw this.getMongoUtils().createDbElementNotFoundError({
					filter: filterQuery,
					updateQuery: updateDict
				}, errMessage);
			}
			this.data = newData;
			return this;
		}
		const newData = await this.db().insertOne(this.data, session);
		this.data = newData;
		this.isNew = false;
		return this;
	}
	db() {
		return this.constructor.db();
	}
	static async bulkWrite(operations, options = {}) {
		if (operations.length === 0) return {
			ok: 1,
			writeErrors: [],
			nUpserted: 0,
			nMatched: 0,
			nModified: 0
		};
		let session;
		if (options.sessionWrapper) {
			if (options.sessionWrapper.type !== this.Type) throw new Error("Invalid session type");
			session = options.sessionWrapper.session;
		}
		const result = await this.db().bulkWrite(operations, {
			ordered: true,
			...session && { session }
		});
		const { ok, upsertedCount: nUpserted, matchedCount: nMatched, modifiedCount: nModified } = result;
		return {
			ok,
			writeErrors: result.getWriteErrors(),
			nUpserted,
			nMatched,
			nModified
		};
	}
	static async findOne(filterQuery, projection, options) {
		const fullValidation = filterQuery.$fullValidation;
		const { query, options: findOneOptions } = this.buildCleanQueryAndOptions(filterQuery, projection);
		const data = await this.db().findOne(query, {
			...findOneOptions,
			...options
		});
		if (data) return new this(data, { ...fullValidation && { fullValidation } });
	}
	static async defFindOne(filterQuery, notFoundErrMessage, projection, options) {
		const model = await this.findOne(filterQuery, projection, options);
		if (model) return model;
		const modelName = ModelTypeHelper.name(this.Type);
		const errMessage = notFoundErrMessage || `The ${modelName} you were looking for could not be found.`;
		throw this.getMongoUtils().createDbElementNotFoundError({
			filter: filterQuery,
			...projection && { projection }
		}, errMessage);
	}
	static async findOneProjectedData(filterQuery, props, options) {
		const projection = props.reduce((acc, prop) => {
			return {
				...acc,
				[prop]: true
			};
		}, { _id: true });
		const { query, options: findOneOptions } = this.buildCleanQueryAndOptions(filterQuery, projection);
		return await this.db().findOne(query, {
			...findOneOptions,
			...options
		});
	}
	static async defFindOneProjectedData(filterQuery, props, notFoundErrMessage, options) {
		const projectedData = await this.findOneProjectedData(filterQuery, props, options);
		if (projectedData) return projectedData;
		const modelName = ModelTypeHelper.name(this.Type);
		const errMessage = notFoundErrMessage || `The ${modelName} you were looking for could not be found.`;
		throw this.getMongoUtils().createDbElementNotFoundError({
			filter: filterQuery,
			...props && { props }
		}, errMessage);
	}
	static getMongoUtils() {
		throw new Error("not implemented");
	}
	static async findIn(ids, query = {}, projection, options) {
		if ((ids || []).length < 1) return [];
		const filterQuery = {
			_id: { $in: ids },
			...query || {}
		};
		return this.find(filterQuery, projection, options);
	}
	static async findInProjectedData(ids, props, query = {}, options) {
		if (!ids.length) return [];
		const filterQuery = {
			_id: { $in: ids },
			...query || {}
		};
		return this.findProjectedData(filterQuery, props, options);
	}
	static async find(filterQuery, projection, options) {
		return this.getModelArray(this.getCursor(filterQuery, projection, options), filterQuery?.$fullValidation);
	}
	static async findProjectedData(filterQuery, props, options) {
		const projection = props.reduce((acc, prop) => {
			return {
				...acc,
				[prop]: true
			};
		}, { _id: true });
		return await this.getCursor(filterQuery, projection, options).toArray();
	}
	static getCursor(filterQuery, projection, options) {
		const { query, options: cursorOptions } = this.buildCleanQueryAndOptions(filterQuery, projection);
		return this.db().find(query, {
			...cursorOptions,
			...options
		});
	}
	static async getModelArray(cursorWrapper, fullValidation) {
		return (await cursorWrapper.toArray()).map((modelJson) => {
			return new this(modelJson, { ...fullValidation && { fullValidation } });
		});
	}
	static async findOneAndUpdate(filterQuery, updateDict, options = {}) {
		const fullValidation = filterQuery.$fullValidation;
		const filter = filterQuery;
		const updateQuery = updateDict;
		const newData = await this.db().findOneAndUpdate(filter, updateQuery, {
			returnDocument: "after",
			...options
		});
		if (!newData) return;
		return new this(newData, { ...fullValidation && { fullValidation } });
	}
	static async findOneAndUpdateProjected(filterQuery, updateDict, props) {
		const filter = filterQuery;
		const updateQuery = updateDict;
		const projection = props.reduce((acc, prop) => {
			return {
				...acc,
				[prop]: true
			};
		}, { _id: true });
		const newData = await this.db().findOneAndUpdate(filter, updateQuery, {
			returnDocument: "after",
			projection
		});
		if (!newData) return;
		return newData;
	}
	static async forEachLimit(filterQuery, projection, limit, asyncIteratee, options) {
		if (limit < 1) throw new Error("Limit must be at least 1");
		if (!Number.isInteger(limit)) throw new Error("Limit must be an integer");
		const fullValidation = filterQuery.$fullValidation;
		const cursorWrapper = this.getCursor(filterQuery, projection, options);
		let modelCount = 0;
		let hasNext = true;
		const iterateeTest = async () => {
			if (!hasNext) return false;
			hasNext = await cursorWrapper.hasNext();
			return hasNext;
		};
		const iterateeWrapper = async () => {
			const modelJson = await cursorWrapper.next();
			if (!modelJson) return;
			modelCount++;
			await asyncIteratee(new this(modelJson, { ...fullValidation && { fullValidation } }));
		};
		await Promise.all([...Array(limit)].map(() => BiteAsync.whilst(iterateeTest, iterateeWrapper)));
		return modelCount;
	}
	static async update(filterQuery, updateDict, options = {}) {
		const filter = filterQuery;
		const updateQuery = updateDict;
		return this.db().updateMany(filter, updateQuery, options);
	}
	static async updateStoredData(filterQuery, updateDict, options = {}) {
		const filter = filterQuery;
		const updateQuery = updateDict;
		return this.db().updateMany(filter, updateQuery, options);
	}
	static async findStoredData(filterQuery, options) {
		const { query, options: findOptions } = this.buildCleanQueryAndOptions(filterQuery);
		return this.db().find(query, {
			...findOptions,
			...options
		}).toArray();
	}
	static async findStoredDataOn(clientKey, filterQuery, options) {
		const { query, options: findOptions } = this.buildCleanQueryAndOptions(filterQuery);
		const client = this.getMongoUtils().getMongoClient(clientKey);
		if (!client) throw new Error(`Mongo client "${clientKey}" is not initialized`);
		return client.getCollection(this.CollectionName, this.Type).find(query, {
			...findOptions,
			...options
		}).toArray();
	}
	static async updateWithPipeline(filterQuery, pipeline, options = {}) {
		const filter = filterQuery;
		const stages = pipeline.some((stage) => "$set" in stage && "updatedAt" in stage.$set) ? pipeline : [...pipeline, { $set: { updatedAt: Date.now() } }];
		return this.db().updateMany(filter, stages, options);
	}
	static db() {
		return this.getMongoUtils().getMongoClient(this.DatabaseClientKey).getCollection(this.CollectionName, this.Type);
	}
	static buildCleanQueryAndOptions(filterQuery, projection) {
		let phiProjection = projection;
		if (!Object.values(projection ?? {}).some((value) => value === true)) {
			if ((this.ExclusionFields.length ?? 0) > 0) {
				phiProjection = { ...projection || {} };
				this.ExclusionFields.forEach((field) => {
					phiProjection[field] = false;
				});
			}
		}
		const options = { ...phiProjection && { projection: phiProjection } };
		const { $hint, $limit, $skip, $sort, $fullValidation, $collation, ...queryRemains } = filterQuery || {};
		const query = queryRemains;
		if ($hint) if (EnvironmentHelper.current() === Environment.TEST) options.hint = $hint;
		else Log.warn("Hinting mongo indexes is bad practice and should be avoided in non-test environments");
		if ($limit !== void 0) options.limit = $limit;
		if ($skip !== void 0) options.skip = Number.isFinite($skip) ? Math.max(0, Math.floor($skip)) : 0;
		if ($sort) options.sort = $sort;
		if ($collation) options.collation = {
			locale: "en",
			...$collation
		};
		return {
			options,
			query
		};
	}
};
//#endregion
export { BaseAbstractModel };

//# sourceMappingURL=base_abstract_model.js.map